import React, { useMemo } from 'react';
import type { RouteSheetPrintData, RouteSheetStageRow } from '@/services/workOrder.service';
import { flattenRouteSheetOperatorPages, formatItemDescriptionListBreaks } from '@/utils/routeSheetOperatorPages';
import './PrintableRouteSheetOperatorPages.css';

export type PrintableRouteSheetOperatorPagesProps = {
  data: RouteSheetPrintData;
  companyLegalName: string;
  companyLogoUrl?: string;
};

function dash(v: unknown): string {
  if (v == null || v === '') return '—';
  return String(v);
}

function MetaRow({
  label,
  value,
  valueClassName,
  alwaysShow,
}: {
  label: string;
  value: string;
  valueClassName?: string;
  alwaysShow?: boolean;
}) {
  if (!alwaysShow && (!value || value === '—')) return null;
  return (
    <tr>
      <th>{label}</th>
      <td className={valueClassName}>{value || '—'}</td>
    </tr>
  );
}

const DEVIATION_OPTIONS = ['RFD', 'EMAIL', 'N/A'] as const;

function DeviationCheckRow({ recorded }: { recorded: string | null | undefined }) {
  const recordedText = recorded != null ? String(recorded).trim() : '';
  return (
    <tr>
      <th>Deviation</th>
      <td>
        <span className="rs-operator-checks">
          {DEVIATION_OPTIONS.map((opt) => (
            <span key={opt} className="rs-operator-check">
              <span className="rs-operator-check__box" aria-hidden />
              {opt}
            </span>
          ))}
        </span>
        {recordedText ? <span className="rs-operator-check__recorded">(Recorded: {recordedText})</span> : null}
      </td>
    </tr>
  );
}

function ItemMetaRow({ code, name }: { code: string | null | undefined; name: string | null | undefined }) {
  const codeText = code != null && String(code).trim() ? String(code).trim() : '';
  const nameText = name != null && String(name).trim() ? String(name).trim() : '';
  if (!codeText && !nameText) return null;

  const formattedName = nameText ? formatItemDescriptionListBreaks(nameText) : '';
  const nameLines = formattedName ? formattedName.split('\n') : [];

  return (
    <tr>
      <th>Item</th>
      <td className="rs-operator-item">
        {codeText ? <span className="rs-operator-item__code">{codeText}</span> : null}
        {codeText && nameLines.length > 0 ? <span className="rs-operator-item__sep"> — </span> : null}
        {nameLines.length > 0 ? (
          <span className="rs-operator-item__name">
            {nameLines.map((line, i) => (
              <React.Fragment key={`item-line-${i}`}>
                {i > 0 ? <br /> : null}
                {line}
              </React.Fragment>
            ))}
          </span>
        ) : null}
      </td>
    </tr>
  );
}

function stageDisplayLabel(op: RouteSheetStageRow): string | null {
  const stageName = op.stage_name != null ? String(op.stage_name).trim() : '';
  const stageCode = op.stage_code != null ? String(op.stage_code).trim() : '';
  const opName = op.operation_name != null ? String(op.operation_name).trim() : '';
  if (!stageName && !stageCode) return null;
  if (stageCode && stageName) return `${stageCode} — ${stageName}`;
  if (stageName && opName && stageName.toLowerCase() === opName.toLowerCase()) {
    return stageCode || stageName;
  }
  return stageName || stageCode || null;
}

function OperationsTable({ operations }: { operations: RouteSheetStageRow[] }) {
  if (!operations.length) {
    return <p className="rs-operator-notes">No operations or stages on this BOQ item.</p>;
  }

  const hasDistinctStage = operations.some((op) => {
    const label = stageDisplayLabel(op);
    return Boolean(label);
  });

  return (
    <table className="rs-operator-ops">
      <thead>
        <tr>
          <th style={{ width: '8%' }}>Op #</th>
          <th style={{ width: hasDistinctStage ? '36%' : '92%' }}>Operation</th>
          {hasDistinctStage ? <th style={{ width: '56%' }}>Stage</th> : null}
        </tr>
      </thead>
      <tbody>
        {operations.map((op) => {
          const notes = String(op.remarks ?? '').trim();
          const stageLabel = stageDisplayLabel(op);
          return (
            <tr key={op.stage_id || `${op.operation_no}-${op.operation_name}-${op.stage_code || ''}`}>
              <td className="num">{op.operation_no}</td>
              <td>
                <strong>{op.operation_name}</strong>
                {!hasDistinctStage && notes ? (
                  <div className="rs-operator-op-notes">
                    <span className="rs-operator-op-notes__label">Notes:</span>{' '}
                    {notes.split('\n').map((line, i) => (
                      <React.Fragment key={`${op.stage_id}-n-${i}`}>
                        {i > 0 ? <br /> : null}
                        {line}
                      </React.Fragment>
                    ))}
                  </div>
                ) : null}
              </td>
              {hasDistinctStage ? (
                <td>
                  {stageLabel ? <strong>{stageLabel}</strong> : <span className="rs-operator-muted">—</span>}
                  {notes ? (
                    <div className="rs-operator-op-notes">
                      <span className="rs-operator-op-notes__label">Notes:</span>{' '}
                      {notes.split('\n').map((line, i) => (
                        <React.Fragment key={`${op.stage_id}-sn-${i}`}>
                          {i > 0 ? <br /> : null}
                          {line}
                        </React.Fragment>
                      ))}
                    </div>
                  ) : null}
                </td>
              ) : null}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function verificationLabel(m: { verified?: boolean; not_verified?: boolean }): string {
  if (m.verified) return 'Verified';
  if (m.not_verified) return 'Not verified';
  return '—';
}

function MaterialsTable({
  entries,
}: {
  entries: Array<{
    drawing_line_no?: string | null;
    client_material_specification?: string | null;
    material_grade?: string | null;
    heat_no?: string | null;
    verified?: boolean;
    not_verified?: boolean;
    remarks?: string | null;
  }>;
}) {
  if (!entries.length) {
    return <p className="rs-operator-notes">No material utilization recorded.</p>;
  }
  return (
    <table className="rs-operator-ops">
      <thead>
        <tr>
          <th style={{ width: '12%' }}>Drawing line no.</th>
          <th style={{ width: '26%' }}>Client material specification</th>
          <th style={{ width: '14%' }}>Material grade</th>
          <th style={{ width: '12%' }}>Heat no.</th>
          <th style={{ width: '12%' }}>Verification</th>
          <th style={{ width: '24%' }}>Remarks</th>
        </tr>
      </thead>
      <tbody>
        {entries.map((m, i) => (
          <tr key={`${m.drawing_line_no || ''}-${m.heat_no || ''}-${i}`}>
            <td>{dash(m.drawing_line_no)}</td>
            <td>{dash(m.client_material_specification)}</td>
            <td>{dash(m.material_grade)}</td>
            <td>{dash(m.heat_no)}</td>
            <td>{verificationLabel(m)}</td>
            <td>{dash(m.remarks)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function MrMaterialsTable({
  entries,
}: {
  entries: Array<{
    material?: string | null;
    requested_qty?: string | null;
    issued_qty?: string | null;
    heat_number?: string | null;
    request_number?: string | null;
    boq_line_label?: string | null;
    remarks?: string | null;
  }>;
}) {
  if (!entries.length) {
    return <p className="rs-operator-notes">No material requests for this BOQ line.</p>;
  }
  return (
    <table className="rs-operator-ops">
      <thead>
        <tr>
          <th style={{ width: '28%' }}>Item</th>
          <th style={{ width: '14%' }}>Requested</th>
          <th style={{ width: '14%' }}>Issued</th>
          <th style={{ width: '12%' }}>Heat</th>
          <th style={{ width: '14%' }}>MR #</th>
          <th style={{ width: '18%' }}>Remarks</th>
        </tr>
      </thead>
      <tbody>
        {entries.map((m, i) => (
          <tr key={`${m.request_number || ''}-${m.material || ''}-${i}`}>
            <td>{dash(m.material)}</td>
            <td>{dash(m.requested_qty)}</td>
            <td>{dash(m.issued_qty)}</td>
            <td>{dash(m.heat_number)}</td>
            <td>{dash(m.request_number)}</td>
            <td>{dash(m.remarks)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

const HANDWRITE_FIELDS = [
  'Welding:',
  'DIR No:',
  'Surface Treatment (painting & Any other):',
  'COC No:',
] as const;

function HandwriteFields() {
  return (
    <div className="rs-operator-handwrite">
      {HANDWRITE_FIELDS.map((label) => (
        <div key={label} className="rs-operator-handwrite__row">
          <span className="rs-operator-handwrite__label">{label}</span>
          <span className="rs-operator-handwrite__line" />
        </div>
      ))}
    </div>
  );
}

/** One landscape A4 page per BOQ item for floor-operator handoff. */
export const PrintableRouteSheetOperatorPages: React.FC<PrintableRouteSheetOperatorPagesProps> = ({
  data,
  companyLegalName,
}) => {
  const pages = useMemo(() => flattenRouteSheetOperatorPages(data), [data]);
  const h = data.header;
  const total = pages.length || 1;

  const pageList =
    pages.length > 0
      ? pages
      : [
          {
            groupLabel: '—',
            item_code: h.item_code ?? null,
            revision: h.item_revision ?? null,
            boq_line_no: h.boq_line_no ?? null,
            item_description: h.item_name ?? null,
            drawing_no: h.drawing_no ?? null,
            quantity: h.quantity,
            uom: h.uom,
            operations: [] as RouteSheetStageRow[],
            material_entries: [],
            mr_materials: [],
          },
        ];

  return (
    <div className="rs-operator-root">
      {pageList.map((page, idx) => {
        const itemCode = page.item_code ?? h.item_code;
        const itemName = page.item_description ?? h.item_name;
        const itemRevision = page.revision ?? h.item_revision;
        const drawingNo = page.drawing_no ?? h.drawing_no;
        const qty =
          page.quantity != null
            ? `${page.quantity} ${page.uom || h.uom || ''}`.trim()
            : `${h.quantity} ${h.uom || ''}`.trim();

        const lineSr =
          page.boq_line_no != null && String(page.boq_line_no).trim()
            ? String(page.boq_line_no).trim()
            : '';
        const woLineRef =
          h.work_order_number && lineSr ? `${h.work_order_number}-${lineSr}` : '';

        return (
          <section
            key={page.boq_line_no || page.item_code || `boq-${idx}`}
            className="rs-operator-page"
            data-page={idx + 1}
          >
            <h1 className="rs-operator-page__title">OPERATOR ROUTE SHEET</h1>
            <div className="rs-operator-page__chrome">
              <span>
                {companyLegalName} · {woLineRef || h.route_sheet_no || h.work_order_number} · RS
                Rev {h.revision_no}
              </span>
              <span>
                Page {idx + 1} of {total}
              </span>
            </div>

            <div>
              <div className="rs-operator-page__section-title">Work order</div>
              <table className="rs-operator-meta">
                <tbody>
                  <MetaRow label="Work order" value={dash(h.work_order_number)} />
                  <MetaRow label="WO line ref" value={dash(woLineRef)} alwaysShow={Boolean(woLineRef)} />
                  <MetaRow label="Customer order" value={dash(h.sales_order_number)} />
                  <MetaRow label="Production request" value={dash(h.production_request_number)} />
                  <MetaRow label="Project" value={dash(h.project_name)} />
                  <ItemMetaRow code={itemCode} name={itemName} />
                  <MetaRow label="Revision" value={dash(itemRevision)} />
                  <MetaRow label="Drawing no" value={dash(drawingNo)} />
                  <MetaRow label="Quantity" value={qty || '—'} />
                  <DeviationCheckRow recorded={h.deviation_type} />
                </tbody>
              </table>
            </div>

            <div>
              <div className="rs-operator-page__section-title">
                Client requirements (scope of work)
              </div>
              <p className="rs-operator-notes">{dash(h.client_scope_of_work)}</p>
            </div>

            <div>
              <div className="rs-operator-page__section-title">Quality requirements</div>
              <p className="rs-operator-notes">{dash(h.client_quality_requirements)}</p>
              {h.wo_customer_requirements_notes ? (
                <p className="rs-operator-notes">
                  <strong>Customer notes:</strong> {h.wo_customer_requirements_notes}
                </p>
              ) : null}
            </div>

            <div>
              <div className="rs-operator-page__section-title">Material utilization</div>
              <MaterialsTable entries={page.material_entries || []} />
            </div>

            <div>
              <div className="rs-operator-page__section-title">Material requests</div>
              <MrMaterialsTable entries={page.mr_materials || []} />
            </div>

            <div>
              <div className="rs-operator-page__section-title">Operations &amp; Stages</div>
              <OperationsTable operations={page.operations} />
            </div>

            <div>
              <div className="rs-operator-page__section-title">Quality Documents</div>
              <HandwriteFields />
            </div>

            <footer className="rs-operator-sign">
              <div className="rs-operator-sign__box">
                <label>Production Supervisor</label>
                <span className="rs-operator-sign__line" />
                <span className="rs-operator-sign__date-label">Date</span>
                <span className="rs-operator-sign__line rs-operator-sign__line--date" />
              </div>
              <div className="rs-operator-sign__box">
                <label>Quality Engineer</label>
                <span className="rs-operator-sign__line" />
                <span className="rs-operator-sign__date-label">Date</span>
                <span className="rs-operator-sign__line rs-operator-sign__line--date" />
              </div>
            </footer>
          </section>
        );
      })}
    </div>
  );
};
